/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { DocumentParseClientType } from '../models/DocumentParseClientType';
import type { DocumentParseResponseFileOverride } from '../models/DocumentParseResponseFileOverride';
import type { DocumentParseStatus } from '../models/DocumentParseStatus';
import type { DocumentParseStatusResponse } from '../models/DocumentParseStatusResponse';
import type { SheetOverridesRequest } from '../models/SheetOverridesRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class DocumentParseService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Parse Document
     * Parse document with smart deduplication. Returns existing parse if available, otherwise starts new parse.
     *
     * Args:
     * cluster_id: ID of the cluster (from route)
     * document_id: ID of the document to parse
     * sheets: Optional list of sheet names to parse (for selective parsing of large files)
     * metadata_only: If True, return metadata only (describe mode) instead of full parse
     * parse_service: Optional document parse service
     *
     * Returns:
     * DocumentParseStatusResponse with parse status
     *
     * Raises:
     * 404: Document not found
     * 422: Document cannot be parsed
     * @param clusterId
     * @param documentId
     * @param sheets Optional list of sheet names to parse (case-sensitive)
     * @param metadataOnly Return metadata only (fast describe mode)
     * @returns DocumentParseStatusResponse Successful Response
     * @throws ApiError
     */
    public parseDocument(
        clusterId: string,
        documentId: string,
        sheets?: (Array<string> | null),
        metadataOnly: boolean = false,
    ): CancelablePromise<DocumentParseStatusResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/document-parse/parse-document/{document_id}',
            path: {
                'cluster_id': clusterId,
                'document_id': documentId,
            },
            query: {
                'sheets': sheets,
                'metadata_only': metadataOnly,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Parse Status
     * Get the status of a document parse.
     *
     * Args:
     * cluster_id: ID of the cluster (from route)
     * parse_id: ID of the parse record
     * parse_service: DocumentParseService
     *
     * Returns:
     * DocumentParseStatusResponse with current status
     * @param clusterId
     * @param parseId
     * @returns DocumentParseStatusResponse Successful Response
     * @throws ApiError
     */
    public getParseStatus(
        clusterId: string,
        parseId: string,
    ): CancelablePromise<DocumentParseStatusResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/document-parse/parse-status/{parse_id}',
            path: {
                'cluster_id': clusterId,
                'parse_id': parseId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Cancel Parse
     * Cancel any in-progress parse(s) for the document.
     *
     * Marks PENDING/PROCESSING/RETRYING rows as FAILED so a subsequent reparse
     * creates a fresh record. Safe to call when nothing is in-flight — returns
     * canceled=0 in that case.
     * @param clusterId
     * @param documentId
     * @returns any Successful Response
     * @throws ApiError
     */
    public cancelParse(
        clusterId: string,
        documentId: string,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/document-parse/cancel-parse/{document_id}',
            path: {
                'cluster_id': clusterId,
                'document_id': documentId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Retry Parse
     * Retry a failed document parse.
     *
     * Args:
     * cluster_id: ID of the cluster (from route)
     * parse_id: ID of the parse record to retry
     * parse_service: Document parse service
     *
     * Returns:
     * DocumentParseStatusResponse with updated status
     * @param clusterId
     * @param parseId
     * @returns DocumentParseStatusResponse Successful Response
     * @throws ApiError
     */
    public retryParse(
        clusterId: string,
        parseId: string,
    ): CancelablePromise<DocumentParseStatusResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/document-parse/retry-parse/{parse_id}',
            path: {
                'cluster_id': clusterId,
                'parse_id': parseId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Parses For Cluster
     * List all document parses for a cluster.
     *
     * Args:
     * cluster_id: ID of the cluster (from route)
     * parse_service: DocumentParseService
     *
     * Returns:
     * List of DocumentParseStatusResponse
     * @param clusterId
     * @returns DocumentParseStatusResponse Successful Response
     * @throws ApiError
     */
    public listParsesForCluster(
        clusterId: string,
    ): CancelablePromise<Array<DocumentParseStatusResponse>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/document-parse/list-parses',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Download Parse Result
     * Download the parsed result file.
     *
     * Args:
     * cluster_id: ID of the cluster (from route)
     * parse_id: ID of the parse record
     * parse_service: DocumentParseService
     * file_service: FileService instance
     *
     * Returns:
     * Redirect to presigned download URL
     * @param clusterId
     * @param parseId
     * @returns any Successful Response
     * @throws ApiError
     */
    public downloadParseResult(
        clusterId: string,
        parseId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/document-parse/download-parse/{parse_id}',
            path: {
                'cluster_id': clusterId,
                'parse_id': parseId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Parse Result
     * Get the parsed result data as JSON.
     *
     * This endpoint returns the parsed document data directly as JSON, automatically
     * handling gzip decompression if the stored result file is compressed.
     *
     * Args:
     * cluster_id: ID of the cluster (from route)
     * parse_id: ID of the parse record
     * parse_service: DocumentParseService
     *
     * Returns:
     * Parsed result data as JSON object:
     * - For Excel files: GridGrazerResponse format
     * - For other document types: format depends on the parse client used
     *
     * Raises:
     * 404: If parse record not found or the parse result not available
     * 500: If there's an error reading or decompressing the result file
     * @param clusterId
     * @param parseId
     * @returns any Successful Response
     * @throws ApiError
     */
    public getParseResult(
        clusterId: string,
        parseId: string,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/document-parse/parse-result/{parse_id}',
            path: {
                'cluster_id': clusterId,
                'parse_id': parseId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Check Document Parse
     * Check if a document has a parse matching the specified criteria.
     *
     * Args:
     * cluster_id: ID of the cluster (from route)
     * document_id: ID of the document to check
     * parse_client: Optional specific parse client to check
     * sheets: Optional list of sheet names. If provided, only return parse that matches these sheets.
     * statuses: Optional list of statuses to filter by. If not provided, return any status.
     * parse_service: Optional specific parse client to check
     *
     * Returns:
     * DocumentParseStatusResponse if a matching parse exists, None otherwise
     * @param clusterId
     * @param documentId
     * @param sheets Optional list of sheet names to match. If not provided, returns any parse without sheet filtering.
     * @param statuses Optional list of statuses to filter by. If not provided, returns parses with any status. If provided, only returns parse with one of these statuses.
     * @param parseClient Specific parse client to check. If not provided, will determine based on current file type.
     * @returns any Successful Response
     * @throws ApiError
     */
    public checkDocumentParse(
        clusterId: string,
        documentId: string,
        sheets?: (Array<string> | null),
        statuses?: (Array<DocumentParseStatus> | null),
        parseClient?: (DocumentParseClientType | null),
    ): CancelablePromise<(DocumentParseStatusResponse | null)> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/document-parse/check-parse/{document_id}',
            path: {
                'cluster_id': clusterId,
                'document_id': documentId,
            },
            query: {
                'sheets': sheets,
                'statuses': statuses,
                'parse_client': parseClient,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Parse Overrides
     * Save spreadsheet overrides for a parsed document.
     *
     * This endpoint saves user-modified spreadsheet metadata such as:
     * - Sheet visibility (hidden/visible)
     * - Frozen panes configuration
     * - Row/column visibility and dimensions
     *
     * The overrides are stored separately from the original parse result
     * and are applied when the parse result is retrieved.
     *
     * Args:
     * cluster_id: ID of the cluster (from route)
     * parse_id: ID of the parse record
     * overrides: Sheet overrides to save
     * parse_service: Optional specific parse client to save
     * override_service: Optional specific parse client to save
     *
     * Returns:
     * DocumentParseResponseFileOverrideRead with saved overrides
     *
     * Raises:
     * 404: If parse record not found or no response file exists
     * 400: If override validation fails
     * @param clusterId
     * @param parseId
     * @param requestBody
     * @returns DocumentParseResponseFileOverride Successful Response
     * @throws ApiError
     */
    public saveParseOverrides(
        clusterId: string,
        parseId: string,
        requestBody: SheetOverridesRequest,
    ): CancelablePromise<DocumentParseResponseFileOverride> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/document-parse/parse/{parse_id}/overrides',
            path: {
                'cluster_id': clusterId,
                'parse_id': parseId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
